<template>
    <div class="modal-overlay">
        <div class="modal-card">
            <div class="modal-header">
                <h6 class="modal-title"><i class="fa fa-plus-circle"></i> Add New Submission</h6>
                <button class="modal-close" @click="$emit('close')">
                    <i class="fa fa-times"></i>
                </button>
            </div>
            <div class="modal-body">
                <div class="d-flex align-items-end gap-2">
                    <div class="flex-grow-1">
                        <label class="field-label">Sales Submit To:</label>
                        <input
                            v-model="allocatedStaffId"
                            type="number"
                            class="form-control"
                            placeholder="Enter Contact Profile ID"
                        />
                    </div>
                    <button @click="onClickGo" :disabled="goLoading" class="btn btn-primary btn-sm py-1 px-1">
                        {{ goLoading ? '...' : 'GO' }}
                    </button>
                </div>

                <template v-if="salesPersonProfile">
                    <div class="mb-2">
                        <label class="field-label">Allocate Amount</label>
                        <input
                            v-model="amount"
                            type="number"
                            class="form-control"
                            placeholder="Enter Amount"
                        />
                    </div>
                </template>
            </div>
            <div class="modal-footer">
                <button @click="$emit('close')" class="btn btn-outline-secondary btn-sm">Cancel</button>
                <button
                    v-if="salesPersonProfile"
                    @click="submit"
                    :disabled="saveLoading"
                    class="btn btn-primary btn-sm"
                >{{ saveLoading ? 'Saving...' : 'Save' }}</button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { inject, ref } from 'vue';
import { useRoute } from 'vue-router';
import handleSdm from '@/services/modules/sdm/sdm';

const props = defineProps({
    staffId: {
      type: [Number, String],
      default: null
    },
    month: {
      type: String,
      default: ''
    },
});

const emit = defineEmits(['close', 'saved']);
const route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { fetchSaleAllocations, storeSaleAllocations } = handleSdm();

const allocatedStaffId = ref('');
const amount = ref('');
const contactProfile = ref(null);
const salesPersonProfile = ref(null);
const goLoading = ref(false);
const saveLoading = ref(false);

const getQuery = () => {
    let q = `?company_id=${route.params.companyId}`;
    if (props.staffId) q += `&staff_id=${props.staffId}`;
    if (allocatedStaffId.value) q += `&allocated_staff_id=${allocatedStaffId.value}`;
    q += `&month=${props.month}`;
    return q;
};

const onClickGo = async () => {
    if (!allocatedStaffId.value) {
      showError('Sale Submit to ID is required.');
      return;
    }
    if (!props.month) {
      showError('Please select a month first.');
      return;
    }

    goLoading.value = true;
    contactProfile.value = null;
    amount.value = '';

    await fetchSaleAllocations(getQuery())
        .then(res => {
            if (!res.status) {
              showError(res.message);
              return;
            }
            salesPersonProfile.value = res.data.allocated_contact_profile;
            contactProfile.value = res.data.contact_profile;
        })
        .catch(() => {
          showError('Something went wrong.');
        })
        .finally(() => {
          goLoading.value = false;
        });
};

   const submit = async () => {
    if (!amount.value) {
      showError('Allocate Amount is required.');
      return;
    }

    saveLoading.value = true;

    const payload = {
        company_id: route.params.companyId,
        contact_profile_id: salesPersonProfile.value.id,
        sales_person_id: contactProfile.value.id,
        amount: amount.value,
        status: 'active',
        date: props.month,
        is_create: true,
        is_profile: true
    };

    await storeSaleAllocations(payload, getQuery())
        .then(res => {
            if (!res.status) {
              showError(res.message);
              return;
            }
            showSuccess(res.message);
            emit('saved');
            emit('close');
        })
        .catch(() => {
          showError('Something went wrong.');
        })
        .finally(() => {
          saveLoading.value = false;
        });
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    backdrop-filter: blur(2px);
}

.modal-card {
    background: #fff;
    border-radius: 14px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
    overflow: hidden;
    margin: 1rem;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    background: linear-gradient(90deg, #1e3a5f, #2d6a9f);
}

.modal-title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 8px;
}

.modal-close {
    background: none;
    border: none;
    color: #fff;
    cursor: pointer;
    font-size: 1rem;
    opacity: 0.8;
    padding: 0;
    line-height: 1;
    transition: opacity 0.15s;
}

.modal-close:hover { opacity: 1; }

.modal-body {
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.modal-footer {
    padding: 0.75rem 1.25rem;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    background: #f8fafc;
    border-top: 1px solid #e5e7eb;
}

.field-row {
    display: flex;
    gap: 0.5rem;
    align-items: flex-end;
}

.field-wrap {
    flex: 1;
}

.field-label {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 4px;
}

.profile-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    color: #1e3a5f;
    font-size: 0.875rem;
    font-weight: 600;
}

.profile-badge i {
    color: #3b82f6;
    font-size: 1.1rem;
}
</style>
